import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../../components/Toast';
import { LabelSelect } from '../../../components/Input/Input';
import { loadAccountPlans, saveAccountPlan, deleteAccountPlan } from '../../../core/utils/accountPlan';
import { ACCOUNT_PLAN_TYPE_OPTIONS } from '../../../core/constants/financial';
import FinancialListModal from './FinancialListModal';
import AccountPlanFormModal from './AccountPlanFormModal';
import AccountPlanSummary from './AccountPlanSummary';
import AccountPlanTable from './AccountPlanTable';

const FILTER_TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  ...ACCOUNT_PLAN_TYPE_OPTIONS,
];

const INITIAL_FORM = {
  code: '',
  name: '',
  type: 'despesa',
  level: 1,
  status: 'ativa',
  parent_id: null,
};

const AccountPlanModal = ({ isOpen, onClose }) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const fetchAccounts = async () => {
    const data = await loadAccountPlans(company.id);
    setAccounts(data);
  };

  useEffect(() => {
    if (isOpen && company.id) fetchAccounts();
  }, [isOpen, company.id]);

  const filtered = accounts.filter((a) => {
    const term = search.toLowerCase();
    const matchesSearch = !term ||
      a.code?.toLowerCase().includes(term) ||
      a.name?.toLowerCase().includes(term);
    const matchesType = !filterType || a.type === filterType;
    return matchesSearch && matchesType;
  });

  const parentOptions = accounts
    .filter((a) => a.level === 1)
    .map((a) => ({ value: a.id, label: `${a.code} — ${a.name}` }));

  const openForm = (account = null) => {
    if (account) {
      setForm({
        code: account.code || '',
        name: account.name || '',
        type: account.type || 'despesa',
        level: account.level || 1,
        status: account.status || 'ativa',
        parent_id: account.parent_id || null,
      });
      setEditingId(account.id);
    } else {
      setForm(INITIAL_FORM);
      setEditingId(null);
    }
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(INITIAL_FORM);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (saving) return;

    const code = form.code.trim();
    if (!code) {
      alertError('Código é obrigatório.');
      return;
    }
    if (!form.name.trim()) {
      alertError('Descrição é obrigatória.');
      return;
    }

    const duplicate = accounts.find(
      (a) => String(a.code).trim() === code && String(a.id) !== String(editingId)
    );
    if (duplicate) {
      alertError(`Já existe uma conta com o código ${code}: ${duplicate.name}.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        code,
        company_id: company.id,
        created_by: user.id,
      };

      const { error } = await saveAccountPlan(editingId, payload);
      if (error) {
        if (error.code === '23505') {
          alertError(`Já existe uma conta com o código ${code}.`);
        } else {
          alertError('Erro ao salvar conta.');
        }
        return;
      }

      alertSuccess(editingId ? 'Conta atualizada!' : 'Conta cadastrada!');
      closeForm();
      fetchAccounts();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account, onSuccess) => {
    const result = await Swal.fire({
      title: 'Deseja excluir esta conta?',
      text: `${account.code} - ${account.name}`,
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (result.isDenied) {
      const { error } = await deleteAccountPlan(account.id, user.id);
      if (!error) {
        alertSuccess('Conta removida!');
        fetchAccounts();
        if (onSuccess) onSuccess();
      } else {
        alertError('Erro ao remover conta.');
      }
    }
  };

  const filterSelect = (
    <div className="fd-modal-filter">
      <LabelSelect menuPortalTarget={document.body}
        options={FILTER_TYPE_OPTIONS}
        value={FILTER_TYPE_OPTIONS.find((o) => o.value === filterType)}
        placeholder="Todos os tipos"
        onChange={(opt) => setFilterType(opt?.value || '')}
      />
    </div>
  );

  return (
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title="Plano de Contas"
      badge={accounts.length}
      searchPlaceholder="Buscar por código ou nome..."
      search={search}
      onSearchChange={setSearch}
      onNew={() => openForm()}
      newLabel="+ Nova Conta"
      headerExtra={filterSelect}
    >
      <AccountPlanSummary
        accounts={accounts}
        filterType={filterType}
        setFilterType={setFilterType}
      />

      <AccountPlanTable accounts={filtered} onEdit={openForm} />

      {formOpen && (
        <AccountPlanFormModal
          form={form}
          setForm={setForm}
          editingId={editingId}
          onSave={handleSave}
          onClose={closeForm}
          onDelete={editingId ? () => {
            const account = accounts.find((a) => String(a.id) === String(editingId));
            if (account) handleDelete(account, closeForm);
          } : undefined}
          saving={saving}
          parentOptions={parentOptions}
        />
      )}
    </FinancialListModal>
  );
};

export default AccountPlanModal;
